import type { User } from '@/types/user'
import { usersFixture } from '@/fixtures/users.fixture'
import { useAuthStore } from '@/stores/auth.store'
import { simulateLatency } from '@/services/mock/latency'
import { httpClient, setAuthToken } from '@/services/http/httpClient'
import { pickService } from '@/services/http/apiMode'
import { nextId } from '@/services/mock/idGenerator'

export interface RegisterInput {
  firstName: string
  lastName: string
  companyName: string
  email: string
  password: string
}

export interface OnboardingInput {
  companyName: string
  domain: string
  teamSize: string
  channels: string[]
}

/** Login either establishes a session, or hands back a challenge to finish with a code. */
export type LoginResult = { user: User } | { twoFactorChallenge: string }

export interface AuthService {
  login(email: string, _password: string): Promise<LoginResult>
  /** Second step of a two-factor login: exchanges the challenge for a session. */
  completeTwoFactor(challenge: string, code: string): Promise<User>
  /** Creates the company and its owner account in one go — the "Creare companie" step of the main journey. */
  register(input: RegisterInput): Promise<User>
  completeOnboarding(input: OnboardingInput): Promise<User>
  /** Always resolves, whether or not the email exists — never reveal which accounts are real. */
  requestPasswordReset(email: string): Promise<void>
  logout(): Promise<void>
}

/** Accounts created in this browser session (mock only — lost on reload, like every other mock write). */
let registeredUsers: User[] = []


const mockAuthService: AuthService = {
  async login(email) {
    await simulateLatency()
    const user = [...usersFixture, ...registeredUsers].find((u) => u.email.toLowerCase() === email.trim().toLowerCase())
    if (!user) {
      throw {
        code: 'invalid_credentials',
        message: 'Email sau parolă incorecte.',
        fieldErrors: { email: 'Nu găsim un cont cu acest email.' },
      }
    }
    return { user }
  },
  async completeTwoFactor() {
    throw { code: 'not_found', message: 'Verificarea în doi pași nu este disponibilă în modul demo.' }
  },
  async register(input) {
    await simulateLatency(600, 1000)
    const email = input.email.trim().toLowerCase()
    if ([...usersFixture, ...registeredUsers].some((u) => u.email.toLowerCase() === email)) {
      throw {
        code: 'email_taken',
        message: 'Există deja un cont cu acest email.',
        fieldErrors: { email: 'Există deja un cont cu acest email. Conectează-te sau folosește alt email.' },
      }
    }
    const user: User = {
      id: nextId('usr_new'),
      firstName: input.firstName.trim(),
      lastName: input.lastName.trim(),
      email,
      role: 'owner',
      avatarColor: '#2F4CDD',
      companyName: input.companyName.trim(),
      onboardingCompleted: false,
    }
    registeredUsers = [...registeredUsers, user]
    return user
  },
  async completeOnboarding(input) {
    await simulateLatency(300, 600)
    const current = useAuthStoreLazy().user
    if (!current) throw { code: 'unauthorized', message: 'Sesiunea a expirat.' }
    const updated: User = { ...current, companyName: input.companyName.trim() || current.companyName, onboardingCompleted: true }
    registeredUsers = registeredUsers.map((u) => (u.id === updated.id ? updated : u))
    return updated
  },
  async requestPasswordReset() {
    await simulateLatency(500, 900)
  },
  async logout() {
    await simulateLatency(100, 250)
  },
}

/**
 * The real API. Login/register hand back a Sanctum token which every later
 * request carries; logout revokes it server-side as well as locally.
 */
const httpAuthService: AuthService = {
  async login(email, password) {
    const response = await httpClient.post<{ token?: string; user?: User; twoFactorRequired?: boolean; challenge?: string }>(
      '/auth/login',
      { email, password },
    )
    // With a second factor on, the password alone buys only a short-lived challenge.
    if (response.twoFactorRequired && response.challenge) {
      return { twoFactorChallenge: response.challenge }
    }
    setAuthToken(response.token as string)
    return { user: response.user as User }
  },
  async completeTwoFactor(challenge, code) {
    const { token, user } = await httpClient.post<{ token: string; user: User }>('/auth/two-factor/verify', { challenge, code })
    setAuthToken(token)
    return user
  },
  async register(input) {
    const { token, user } = await httpClient.post<{ token: string; user: User }>('/auth/register', input)
    setAuthToken(token)
    return user
  },
  async completeOnboarding(input) {
    await httpClient.post('/tenant/onboarding', {
      companyName: input.companyName,
      domain: input.domain,
      teamSize: input.teamSize,
      channels: input.channels,
    })
    return httpClient.get<User>('/auth/me')
  },
  async requestPasswordReset(email) {
    await httpClient.post('/auth/forgot-password', { email })
  },
  async logout() {
    try {
      await httpClient.post('/auth/logout')
    } finally {
      // Even if revoking fails (expired token, offline), the local session must go.
      setAuthToken(null)
    }
  },
}

export const authService: AuthService = pickService(mockAuthService, httpAuthService)

// Resolved lazily to avoid a static import cycle (store → service → store).
function useAuthStoreLazy() {
  return useAuthStore()
}
